import { useState, useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";
import { NAV_LINKS } from "#/lib/constants";
import { Menu, ArrowUpRight } from "lucide-react";
import { MobileMenu } from "./MobileMenu";

/**
 * Edge-to-edge industrial nav bar, mirrored from the client's reference: solid
 * black, logo hard left, links centered, and a flame block welded to the right
 * edge at full bar height. Height is published as --nav-h so the hero and every
 * page header clear it without guessing.
 */
const LINKS = [{ label: "Home", href: "/#home" }, ...NAV_LINKS] as const;

/** The href's route path, with any #hash fragment stripped. */
function linkPath(href: string): string {
  return href.split("#")[0] || "/";
}

/** True when `href`'s path is the active route for the given pathname. Home
 * ("/#home") only matches the exact home route; every other link matches its
 * own path plus any nested path beneath it (e.g. "/services" stays active on
 * "/services/concrete-driveways"). */
function pathMatches(href: string, pathname: string): boolean {
  const path = linkPath(href);
  if (path === "/") return pathname === "/";
  return pathname === path || pathname.startsWith(`${path}/`);
}

/** The single nav link to highlight for the current pathname. When more than
 * one link shares a path (a plain link and a "#section" deep link to the same
 * page both match), the plain, hash-free link wins so only one link is ever
 * marked current. */
function getActiveHref(links: readonly { href: string }[], pathname: string): string | undefined {
  const matches = links.filter((link) => pathMatches(link.href, pathname));
  if (matches.length <= 1) return matches[0]?.href;
  return (matches.find((link) => !link.href.includes("#")) ?? matches[0]).href;
}

export function StickyNav() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const activeHref = getActiveHref(LINKS, pathname);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 h-[var(--nav-h)] border-b border-white/10 bg-graphite transition-shadow duration-300 ${
          scrolled ? "shadow-lg shadow-black/50" : ""
        }`}
      >
        <div className="flex h-full items-stretch">
          {/* Logo — transparent emblem on the black bar */}
          <a
            href="/#home"
            aria-label="Blue Rock Concrete, home"
            className="flex shrink-0 items-center pl-4 no-underline lg:pl-8"
          >
            <img
              src="/blue-rock-logo-flame.webp"
              alt="Blue Rock Concrete"
              width={480}
              height={273}
              className="h-10 w-auto lg:h-12"
            />
          </a>

          {/* Desktop links, centered */}
          <nav className="hidden flex-1 items-center justify-center gap-1 lg:flex">
            {LINKS.map((link) => {
              const active = link.href === activeHref;
              return (
                <a
                  key={link.href}
                  href={link.href}
                  aria-current={active ? "page" : undefined}
                  className={`px-3.5 py-2 text-sm font-semibold uppercase tracking-wide no-underline transition-colors ${
                    active ? "text-flame" : "text-concrete-light hover:text-white"
                  }`}
                >
                  {link.label}
                </a>
              );
            })}
          </nav>

          {/* Flame block, flush to the right edge, full bar height */}
          <div className="ml-auto flex items-stretch lg:ml-0">
            <a
              href="/quote"
              className="hidden items-center gap-2 bg-flame px-6 font-heading text-sm font-bold uppercase tracking-wide text-white no-underline transition-colors hover:bg-flame-dark sm:inline-flex lg:px-8"
            >
              Get a Free Quote
              <ArrowUpRight className="h-4 w-4" />
            </a>
            <button
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              className="flex w-16 items-center justify-center text-white lg:hidden"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
}
